import { useCallback, useState, useEffect, useImperativeHandle, forwardRef, useRef, useMemo } from 'react'
import { GoogleMap, useJsApiLoader, Marker, OverlayView } from '@react-google-maps/api'
import { Box, Alert, Loader, Center, Text, Button, Group, ActionIcon, Tooltip, useMantineColorScheme } from '@mantine/core'
import { IconCurrentLocation, IconMapPin, IconAlertTriangle } from '@tabler/icons-react'
import { MarkerClusterer } from '@googlemaps/markerclusterer'

// Map container style - full height of parent
const containerStyle = {
    flex: 1,
    width: '100%',
    height: '100%',
    position: 'relative' as const,
}

// Default center (San Francisco) - fallback if geolocation fails
const defaultCenter = {
    lat: 37.7749,
    lng: -122.4194
}

// Light theme map styles
const lightMapStyles = [
    {
        featureType: "poi",
        stylers: [{ visibility: "off" }]
    },
    {
        featureType: "poi.business",
        stylers: [{ visibility: "off" }]
    },
    {
        featureType: "poi.park",
        stylers: [{ visibility: "off" }]
    },
    {
        featureType: "poi.place_of_worship",
        stylers: [{ visibility: "off" }]
    },
    {
        featureType: "poi.school",
        stylers: [{ visibility: "off" }]
    },
    {
        featureType: "poi.medical",
        stylers: [{ visibility: "off" }]
    },
    {
        featureType: "poi.government",
        stylers: [{ visibility: "off" }]
    },
    {
        featureType: "transit",
        stylers: [{ visibility: "off" }]
    }
]

// Dark theme map styles
const darkMapStyles = [
    // Hide POIs (same as light theme)
    {
        featureType: "poi",
        stylers: [{ visibility: "off" }]
    },
    {
        featureType: "poi.business",
        stylers: [{ visibility: "off" }]
    },
    {
        featureType: "poi.park",
        stylers: [{ visibility: "off" }]
    },
    {
        featureType: "poi.place_of_worship",
        stylers: [{ visibility: "off" }]
    },
    {
        featureType: "poi.school",
        stylers: [{ visibility: "off" }]
    },
    {
        featureType: "poi.medical",
        stylers: [{ visibility: "off" }]
    },
    {
        featureType: "poi.government",
        stylers: [{ visibility: "off" }]
    },
    {
        featureType: "transit",
        stylers: [{ visibility: "off" }]
    },
    // Dark theme styling
    {
        elementType: "geometry",
        stylers: [{ color: "#212121" }]
    },
    {
        elementType: "labels.icon",
        stylers: [{ visibility: "off" }]
    },
    {
        elementType: "labels.text.fill",
        stylers: [{ color: "#757575" }]
    },
    {
        elementType: "labels.text.stroke",
        stylers: [{ color: "#212121" }]
    },
    {
        featureType: "administrative",
        elementType: "geometry",
        stylers: [{ color: "#757575" }]
    },
    {
        featureType: "administrative.country",
        elementType: "labels.text.fill",
        stylers: [{ color: "#9e9e9e" }]
    },
    {
        featureType: "administrative.land_parcel",
        stylers: [{ visibility: "off" }]
    },
    {
        featureType: "administrative.locality",
        elementType: "labels.text.fill",
        stylers: [{ color: "#bdbdbd" }]
    },
    {
        featureType: "road",
        elementType: "geometry.fill",
        stylers: [{ color: "#2c2c2c" }]
    },
    {
        featureType: "road",
        elementType: "labels.text.fill",
        stylers: [{ color: "#8a8a8a" }]
    },
    {
        featureType: "road.arterial",
        elementType: "geometry",
        stylers: [{ color: "#373737" }]
    },
    {
        featureType: "road.highway",
        elementType: "geometry",
        stylers: [{ color: "#3c3c3c" }]
    },
    {
        featureType: "road.highway.controlled_access",
        elementType: "geometry",
        stylers: [{ color: "#4e4e4e" }]
    },
    {
        featureType: "road.local",
        elementType: "labels.text.fill",
        stylers: [{ color: "#616161" }]
    },
    {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: "#000000" }]
    },
    {
        featureType: "water",
        elementType: "labels.text.fill",
        stylers: [{ color: "#3d3d3d" }]
    }
]

// Libraries array - moved outside component to prevent reloads
const libraries: ('places')[] = ['places']

interface GoogleMapWrapperProps {
    onMapClick?: (event: google.maps.MapMouseEvent) => void
    temporaryPin?: { lat: number; lng: number } | null
    spottings?: Array<{ id: string; name?: string; location: { lat: number; lng: number }; imageUrl?: string | null }>
    selectedSpottingId?: string | null
    onBoundsChanged?: (bounds: {
        north: number
        south: number
        east: number
        west: number
    }) => void
    onMarkerClick?: (spottingId: string) => void
    // Location props to share state with parent
    userLocation?: { lat: number; lng: number } | null
    locationLoading?: boolean
    locationError?: string | null
    locationPermission?: PermissionState | null
    onRequestLocation?: () => void
    // PWA props
    shouldShowInstallPrompt?: boolean
    onInstallPromptClick?: () => void
}

export interface GoogleMapWrapperRef {
    recenterToUser: () => void
    getCurrentBounds: () => {
        north: number
        south: number
        east: number
        west: number
    } | null
}

export const GoogleMapWrapper = forwardRef<GoogleMapWrapperRef, GoogleMapWrapperProps>(
    ({ onMapClick, temporaryPin, spottings = [], selectedSpottingId, onBoundsChanged, onMarkerClick, userLocation, locationLoading, locationError, locationPermission, onRequestLocation, shouldShowInstallPrompt, onInstallPromptClick }, ref) => {
        const [map, setMap] = useState<google.maps.Map | null>(null)
        const [center, setCenter] = useState(defaultCenter)
        const [markerClusterer, setMarkerClusterer] = useState<MarkerClusterer | null>(null)
        const markerMapRef = useRef<Map<string, google.maps.Marker>>(new Map())
        const { colorScheme } = useMantineColorScheme()
        const timeoutRef = useRef<number | null>(null)

        // Memoize spottings to prevent unnecessary re-renders when array reference changes
        const memoizedSpottings = useMemo(() => spottings, [
            spottings.length,
            spottings.map(s => `${s.id}-${s.location.lat}-${s.location.lng}`).join(',')
        ])

        // Load Google Maps API
        const { isLoaded, loadError } = useJsApiLoader({
            id: 'google-map-script',
            googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
            libraries,
        })

        // Function to get current map bounds
        const getCurrentBounds = useCallback(() => {
            if (!map) return null

            const bounds = map.getBounds()
            if (!bounds) return null

            const ne = bounds.getNorthEast()
            const sw = bounds.getSouthWest()

            return {
                north: ne.lat(),
                south: sw.lat(),
                east: ne.lng(),
                west: sw.lng()
            }
        }, [map])

        // Handle bounds change with debounce
        const handleBoundsChanged = useCallback(() => {
            const bounds = getCurrentBounds()
            if (bounds) {
                onBoundsChanged?.(bounds)
            }
        }, [getCurrentBounds, onBoundsChanged])

        // Debounced version of bounds change handler
        const debouncedBoundsChanged = useCallback(() => {
            // Clear any existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }

            // Set new timeout for 0.75 seconds
            timeoutRef.current = setTimeout(() => {
                handleBoundsChanged()
            }, 750)
        }, [handleBoundsChanged])

        // Create map options with theme-aware styles
        const mapOptions = {
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            gestureHandling: "greedy" as const,
            styles: colorScheme === 'dark' ? darkMapStyles : lightMapStyles
        }

        // Efficient marker management - only add/remove markers that have changed
        useEffect(() => {
            if (!map || !window.google) return

            const currentSpottingIds = new Set(memoizedSpottings.map(s => s.id))
            const existingMarkerIds = new Set(markerMapRef.current.keys())

            // Remove markers that are no longer in the spottings list
            for (const [id, marker] of markerMapRef.current.entries()) {
                if (!currentSpottingIds.has(id)) {
                    marker.setMap(null)
                    markerMapRef.current.delete(id)
                }
            }

            // Add new markers for spottings that don't exist yet
            const newMarkers: google.maps.Marker[] = []

            for (const spotting of memoizedSpottings) {
                if (!existingMarkerIds.has(spotting.id)) {
                    const isSelected = spotting.id === selectedSpottingId
                    const markerSize = isSelected ? 40 : 32 // Larger marker for selected spotting

                    const marker = new window.google.maps.Marker({
                        position: spotting.location,
                        title: spotting.name,
                        map: null, // Don't add to map yet, clusterer will handle this
                        animation: window.google.maps.Animation.DROP, // Animate new markers
                        icon: {
                            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                                <svg width="${markerSize}" height="${markerSize}" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M16 2C10.48 2 6 6.48 6 12c0 7.5 10 18 10 18s10-10.5 10-18c0-5.52-4.48-10-10-10z" fill="${isSelected ? '#FF6B6B' : '#228BE6'}" stroke="#ffffff" stroke-width="2"/>
                                    <circle cx="16" cy="12" r="4" fill="#ffffff"/>
                                </svg>
                            `),
                            scaledSize: new window.google.maps.Size(markerSize, markerSize),
                            anchor: new window.google.maps.Point(markerSize / 2, markerSize),
                        }
                    })

                    // Add click listener
                    marker.addListener('click', () => onMarkerClick?.(spotting.id))

                    markerMapRef.current.set(spotting.id, marker)
                    newMarkers.push(marker)
                }
            }

            // Update clusterer if we have changes
            if (markerClusterer && (newMarkers.length > 0 || existingMarkerIds.size !== currentSpottingIds.size)) {
                // Get all current markers
                const allCurrentMarkers = Array.from(markerMapRef.current.values())

                // Only update clusterer if there are actual changes
                if (newMarkers.length > 0) {
                    // Add only new markers to existing clusterer
                    markerClusterer.addMarkers(newMarkers)
                }

                // If markers were removed, we need to refresh the clusterer
                if (existingMarkerIds.size !== currentSpottingIds.size) {
                    markerClusterer.clearMarkers()
                    markerClusterer.addMarkers(allCurrentMarkers)
                }
            } else if (!markerClusterer && map) {
                // Create initial clusterer with all markers
                const allCurrentMarkers = Array.from(markerMapRef.current.values())
                const clusterer = new MarkerClusterer({
                    map,
                    markers: allCurrentMarkers,
                })

                setMarkerClusterer(clusterer)
            }
        }, [map, memoizedSpottings, onMarkerClick, markerClusterer, selectedSpottingId])

        // Update marker icons when selectedSpottingId changes
        useEffect(() => {
            if (!map || !window.google) return

            for (const [spottingId, marker] of markerMapRef.current.entries()) {
                const isSelected = spottingId === selectedSpottingId
                const markerSize = isSelected ? 40 : 32

                marker.setIcon({
                    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                        <svg width="${markerSize}" height="${markerSize}" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16 2C10.48 2 6 6.48 6 12c0 7.5 10 18 10 18s10-10.5 10-18c0-5.52-4.48-10-10-10z" fill="${isSelected ? '#FF6B6B' : '#228BE6'}" stroke="#ffffff" stroke-width="2"/>
                            <circle cx="16" cy="12" r="4" fill="#ffffff"/>
                        </svg>
                    `),
                    scaledSize: new window.google.maps.Size(markerSize, markerSize),
                    anchor: new window.google.maps.Point(markerSize / 2, markerSize),
                })
            }
        }, [selectedSpottingId, map])

        // Update center when user location is obtained
        useEffect(() => {
            if (userLocation) {
                setCenter(userLocation)

                // Pan to user location if map is already loaded
                if (map) {
                    map.panTo(userLocation)
                    map.setZoom(15) // Zoom in closer for user location
                }
            }
        }, [userLocation, map])

        // Update map styles when theme changes
        useEffect(() => {
            if (map) {
                const newStyles = colorScheme === 'dark' ? darkMapStyles : lightMapStyles
                map.setOptions({ styles: newStyles })
            }
        }, [map, colorScheme])

        const onLoad = useCallback((map: google.maps.Map) => {
            setMap(map)

            // If we already have user location, center on it
            if (userLocation) {
                map.setCenter(userLocation)
                map.setZoom(15)
            }
        }, [userLocation])

        // Set up bounds change listener after map is loaded
        useEffect(() => {
            if (map) {
                // Set up bounds change listener with debounce
                const listener = map.addListener('bounds_changed', debouncedBoundsChanged)

                // Get initial bounds immediately (no debounce for first load)
                setTimeout(() => {
                    handleBoundsChanged()
                }, 100)

                // Cleanup listener and timeout on unmount
                return () => {
                    listener.remove()
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current)
                    }
                }
            }
        }, [map, debouncedBoundsChanged, handleBoundsChanged])

        const onUnmount = useCallback(() => {
            // Clean up all markers
            for (const marker of markerMapRef.current.values()) {
                marker.setMap(null)
            }
            markerMapRef.current.clear()

            setMap(null)
        }, [])

        const handleLocationRequest = () => {
            onRequestLocation?.()
        }

        const handleRecenterToUser = () => {
            if (userLocation && map) {
                map.panTo(userLocation)
                map.setZoom(15)
            }
        }

        // Expose functions to parent component via ref
        useImperativeHandle(ref, () => ({
            recenterToUser: handleRecenterToUser,
            getCurrentBounds
        }), [handleRecenterToUser, getCurrentBounds])

        // Loading state
        if (!isLoaded) {
            return (
                <div className="w-full h-100vh relative overflow-hidden flex">
                    <Center h="100%">
                        <Box ta="center">
                            <Loader size="lg" mb="md" />
                            <Text c="dimmed">Loading Google Maps...</Text>
                        </Box>
                    </Center>
                </div>
            )
        }

        // Error state
        if (loadError) {
            return (
                <div className="w-full h-100vh relative overflow-hidden flex">
                    <Alert color="red" title="Google Maps Error" p="xl">
                        <Text>Failed to load Google Maps. Please check:</Text>
                        <ul>
                            <li>Your API key is set correctly in .env.local</li>
                            <li>Maps JavaScript API is enabled in Google Cloud Console</li>
                            <li>Your domain (localhost:5174) is authorized</li>
                            <li>Billing is enabled in Google Cloud Console</li>
                        </ul>
                        <Text size="sm" c="dimmed" mt="sm">
                            Error: {loadError.message}
                        </Text>
                    </Alert>
                </div>
            )
        }

        return (
            <div className="w-full h-100vh relative overflow-hidden flex">
                {/* Location Controls */}
                <div className="absolute top-4 left-4 z-10">
                    <Group gap="xs">
                        {/* Show location request button if no location yet */}

                        {!userLocation && locationPermission !== 'denied' && (
                            <Button
                                leftSection={<IconCurrentLocation size={16} />}
                                onClick={handleLocationRequest}
                                loading={locationLoading}
                                variant="filled"
                                size="sm"
                            >
                                Use My Location
                            </Button>
                        )}

                        {/* Show recenter button if we have location */}
                        {userLocation && (
                            <Tooltip label="Center on my location" className='bg-transparent'>
                                <ActionIcon
                                    onClick={handleRecenterToUser}
                                    variant="filled"
                                    size="lg"
                                >
                                    <IconMapPin size={18} />
                                </ActionIcon>
                            </Tooltip>
                        )}

                        {/* PWA Install Prompt Button */}
                        {shouldShowInstallPrompt && (
                            <Tooltip label="Install app for best experience" className='bg-transparent'>
                                <ActionIcon
                                    onClick={onInstallPromptClick}
                                    variant="filled"
                                    size="lg"
                                    color="orange"
                                >
                                    <IconAlertTriangle size={18} />
                                </ActionIcon>
                            </Tooltip>
                        )}
                    </Group>
                </div>

                {/* Location Error Alert */}
                {locationError && (
                    <div className="absolute top-16 left-4 right-4 z-10">
                        <Alert color="orange" title="Location Access" onClose={() => { }}>
                            <Text size="sm">{locationError}</Text>
                            {locationPermission === 'denied' && (
                                <Text size="xs" c="dimmed" mt="xs">
                                    You can enable location access in your browser settings and refresh the page.
                                </Text>
                            )}
                        </Alert>
                    </div>
                )}

                <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={center}
                    zoom={userLocation ? 15 : 13} // Zoom in more if we have user location
                    onLoad={onLoad}
                    onUnmount={onUnmount}
                    onClick={onMapClick}
                    options={mapOptions}
                >
                    {/* User location marker */}
                    {userLocation && (
                        <Marker
                            position={userLocation}
                            title="Your Location"
                            icon={{
                                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="16" cy="16" r="12" fill="#4285F4" stroke="#ffffff" stroke-width="3"/>
                                    <circle cx="16" cy="16" r="4" fill="#ffffff"/>
                                </svg>
                            `),
                                scaledSize: new window.google.maps.Size(32, 32),
                                anchor: new window.google.maps.Point(16, 16),
                            }}
                        />
                    )}

                    {/* Temporary pin (shown while user is adding a spotting) */}
                    {temporaryPin && (
                        <Marker
                            position={temporaryPin}
                            title="New Spotting Location"
                            animation={window.google.maps.Animation.DROP}
                            icon={{
                                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M16 2C10.48 2 6 6.48 6 12c0 7.5 10 18 10 18s10-10.5 10-18c0-5.52-4.48-10-10-10z" fill="#FF6B6B" stroke="#ffffff" stroke-width="2"/>
                                    <circle cx="16" cy="12" r="4" fill="#ffffff"/>
                                </svg>
                            `),
                                scaledSize: new window.google.maps.Size(32, 32),
                                anchor: new window.google.maps.Point(16, 32),
                            }}
                        />
                    )}

                    {/* Saved spottings markers are now handled by the MarkerClusterer */}

                    {/* Image thumbnails above markers */}
                    {memoizedSpottings.map((spotting) =>
                        spotting.imageUrl ? (
                            <OverlayView
                                key={`thumbnail-${spotting.id}`}
                                position={spotting.location}
                                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                            >
                                <div
                                    className="relative cursor-pointer transform -translate-x-1/2"
                                    style={{
                                        marginTop: '-60px', // Position above marker
                                        marginLeft: '-20px'
                                    }}
                                    onClick={() => onMarkerClick?.(spotting.id)}
                                >
                                    <img
                                        src={spotting.imageUrl}
                                        alt="Spotting thumbnail"
                                        className="w-10 h-10 object-cover rounded-full border-2 border-white shadow-lg"
                                        style={{
                                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                                        }}
                                    />
                                </div>
                            </OverlayView>
                        ) : null
                    )}
                </GoogleMap>
            </div>
        )
    })

GoogleMapWrapper.displayName = 'GoogleMapWrapper' 